import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { analyticsService, authService } from '../services/api';
import PasswordRequirements from '../components/PasswordRequirements';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    // Validation
    const newErrors: string[] = [];
    if (formData.password !== formData.confirmPassword) {
      newErrors.push('Passwords do not match');
    }
    if (formData.password.length < 8) {
      newErrors.push('Password must be at least 8 characters');
    }
    if (!formData.email.includes('@')) {
      newErrors.push('Please enter a valid email address');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registrationData } = formData;
      const payload = {
        ...registrationData,
        username: registrationData.email,
      };
      const response = await authService.register(payload);
      
      if (response) {
        analyticsService.trackEvent({
          event_name: 'signup_complete',
          page_type: 'signup',
          payload: {
            source: 'register_form',
          },
        }).catch(() => {
          // Ignore analytics errors for signup completion.
        });
        // Registration successful, redirect to login
        navigate('/login', { 
          state: { message: 'Registration successful! Please log in to continue your application.' }
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      setErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage:
          'linear-gradient(115deg, rgba(6,35,25,0.95), rgba(4,16,38,0.93)), url(https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1950&q=80)',
      }}
    >
      <div className="absolute top-0 left-0 w-full flex justify-between items-center px-6 py-4 text-white text-sm z-10">
        <button
          onClick={() => navigate('/')}
          className="font-semibold tracking-wide uppercase text-xs hover:text-emerald-200 transition"
        >
          Muslim Study Abroad
        </button>
        <Link to="/" className="text-white/80 hover:text-emerald-200 transition">
          ← Back to Home
        </Link>
      </div>
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-12 lg:pt-32 lg:pb-20 flex flex-col lg:flex-row gap-12">
        <div className="text-left max-w-2xl text-white">
          <p className="text-sm uppercase tracking-[0.5em] text-emerald-200/80">Muslim Study Abroad</p>
          <h1 className="font-custom-thin text-4xl lg:text-6xl leading-tight mt-4 mb-6 drop-shadow-2xl">
            Begin your pathway to studying abroad.
          </h1>
          <p className="text-base lg:text-lg text-white/85 leading-relaxed max-w-2xl drop-shadow">
            Share just a few details and unlock our Dreaming stage questionnaire, personalized counselor outreach, and access to Muslim-friendly program resources.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
              <p className="text-emerald-200 font-semibold mb-1 tracking-wide">Intention First</p>
              <p className="text-white/80">Tell us who you are, verify your contact info, and we’ll curate next steps in your Dreaming stage.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
              <p className="text-emerald-200 font-semibold mb-1 tracking-wide">Personalized follow-up</p>
              <p className="text-white/80">Once you’re in the system, advisors can invite you to info sessions, scholarship briefings, and immersive previews.</p>
            </div>
          </div>
          <p className="mt-6 text-sm text-white/80 drop-shadow">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-emerald-100 hover:text-white underline">
              Sign in here
            </Link>
          </p>
        </div>

        <div className="flex-1">
          <div className="glass-panel bg-white/95 rounded-3xl shadow-2xl border border-white/40 p-8">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.4em] text-emerald-600">Get started</p>
              <h2 className="text-2xl font-semibold text-slate-900">Create your student profile</h2>
              <p className="text-sm text-slate-500">We’ll guide you to the Dreaming questionnaire right after this step.</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                  <p className="font-semibold mb-2">Please fix the following:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    placeholder="Amina"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    placeholder="Rahman"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Email (used as username)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="you@example.com"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    placeholder="Create a secure password"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    placeholder="Repeat password"
                  />
                </div>
              </div>
              <PasswordRequirements
                password={formData.password}
                confirm={formData.confirmPassword}
                showConfirmMatch
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:opacity-50 transition"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-t-2 border-white rounded-full animate-spin"></span>
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>

              <p className="text-xs text-center text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-emerald-600 hover:text-emerald-500">
                  Sign in here
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
